import { Ordering } from "src/database/models/Ordering";
import { AnyActionableItem, ListNode, TaskGraph, TreeNode, TreeOf, WithAncestry } from "src/types";

export namespace Tree {
  export function fromGraph(rootIds: string[], graph: TaskGraph): TreeOf<AnyActionableItem>[] {
    return Ordering.sortInPlace(
      rootIds.map((id) => ({
        ...graph.tasks[id],
        children: fromGraph(graph.byParentId[id] || [], graph),
      })),
      (item) => item.order,
    );
  }

  export function build<T extends ListNode>(
    nodes: T[],
    parentId: string | null = null,
  ): TreeOf<T>[] {
    const trees: TreeOf<T>[] = [];
    for (const node of nodes) {
      if (node.parentId !== parentId) continue;
      trees.push({
        ...node,
        children: build<T>(nodes, node.id),
      });
    }

    return trees;
  }

  export function disassemble<T extends { id: string }>(
    tree: TreeOf<T>[],
    parentId: string | null = null,
  ) {
    const nodes: (Omit<T, "children"> & {
      parentId: string | null;
      order: number;
    })[] = [];
    for (let i = 0; i < tree.length; i++) {
      const { children, ...item } = tree[i];
      nodes.push({
        ...item,
        parentId,
        order: i,
      });

      nodes.push(...disassemble(children, item.id));
    }

    return nodes;
  }

  export function insertNode<T extends { id: string }>(
    nodes: TreeOf<T>[],
    node: TreeOf<T>,
    parentId: string | null = null,
    position?: number,
  ) {
    if (!parentId) {
      nodes.push(node);
      return;
    }

    const parent = find(nodes, (t) => t.id === parentId);
    if (parent) {
      if (position !== undefined) {
        parent.children.splice(position, 0, node);
      } else {
        parent.children.push(node);
      }
    }
  }

  export function toNodeList<T extends ListNode>(nodes: TreeNode<T>[]) {
    const nodeList: TreeNode<T>[] = [];
    for (const item of nodes) {
      nodeList.push(item, ...toNodeList(item.children));
    }

    return nodeList;
  }

  export function* walk<T>(nodes: TreeOf<T>[]): Generator<TreeOf<T>> {
    for (const item of nodes) {
      yield item;
      yield* walk(item.children);
    }
  }

  export function* walkDepthFirst<T extends ListNode>(
    nodes: TreeNode<T>[],
  ): Generator<TreeNode<T>> {
    for (const item of nodes) {
      yield* walkDepthFirst(item.children);
      yield item;
    }
  }

  export function* walkWithAncestors<T extends ListNode>(
    nodes: TreeNode<T>[],
    ancestry: TreeNode<T>[] = [],
  ): Generator<WithAncestry<T>> {
    for (const item of nodes) {
      yield { ...item, ancestry };
      yield* walkWithAncestors(item.children, [...ancestry, item]);
    }
  }

  export function map<T, U>(nodes: TreeOf<T>[], fn: (node: TreeOf<T>) => TreeOf<U>): TreeOf<U>[] {
    return nodes.map((node) => {
      const mapped = fn(node);
      return {
        ...mapped,
        children: map(node.children, fn),
      };
    });
  }

  export function filter<T extends ListNode>(
    nodes: TreeNode<T>[],
    fn: (node: TreeNode<T>) => boolean,
  ): TreeNode<T>[] {
    const results: TreeNode<T>[] = [];
    for (const item of nodes) {
      if (fn(item)) {
        results.push({
          ...item,
          children: filter(item.children, fn),
        });
      }
    }
    return results;
  }

  export function find<T>(
    nodes: TreeOf<T>[],
    fn: (node: TreeOf<T>) => boolean,
  ): TreeOf<T> | undefined {
    for (const item of walk(nodes)) {
      if (fn(item)) {
        return item;
      }
    }

    return undefined;
  }

  export function findIndex<T extends ListNode>(
    nodes: TreeNode<T>[],
    fn: (node: TreeNode<T>) => boolean,
  ): number {
    let i = 0;
    for (const item of walk(nodes)) {
      if (fn(item)) {
        return i;
      }
      i += 1;
    }
    return -1;
  }

  function deleteNodeLoop<T extends { id: string }>(id: string, nodes: TreeOf<T>[]) {
    for (let i = 0; i < nodes.length; i++) {
      if (nodes[i].id === id) {
        nodes.splice(i, 1);
        return nodes;
      } else {
        nodes[i].children = deleteNodeLoop(id, nodes[i].children);
      }
    }

    return nodes;
  }

  export function deleteNode<T extends { id: string }>(nodes: TreeOf<T>[], id: string) {
    deleteNodeLoop(id, nodes);
  }

  export function updateNode<T extends { id: string }>(nodes: TreeOf<T>[], node: TreeOf<T>) {
    const t = find(nodes, (t) => t.id === node.id);
    if (t) {
      Object.assign(t, node);
    }
  }

  export function getNodeById<T extends { id: string }>(nodes: TreeOf<T>[], id: string) {
    return find(nodes, (t) => t.id === id);
  }
}
